import { useState, type FormEvent } from 'react'
import {
  DEFAULT_VERTEX_LOCATION,
  VERTEX_CHAT_MODEL_CHOICES,
  VERTEX_EMBEDDING_MODEL_CHOICES,
  VertexCredentialsUpdate,
  type VertexCredentialsView,
} from '@rp/contracts'
import { Field } from '../../design'
import { formatDateTime } from '../../lib/time'
import { AI_CREDENTIALS_COPY } from './aiCredentialsCopy'
import { CredentialsFormFooter } from './CredentialsFormFooter'
import { readServiceAccountKey } from './serviceAccountKey'

type VertexField = 'projectId' | 'location' | 'clientEmail' | 'privateKey' | 'chatModel' | 'embeddingModel'
type VertexDraft = Record<VertexField, string>
type VertexErrors = Partial<Record<VertexField, string>>
type PasteState = 'idle' | 'filled' | 'invalid'

const FIELD_ERRORS: Partial<Record<VertexField, string>> = {
  projectId: AI_CREDENTIALS_COPY.invalid.projectId,
  location: AI_CREDENTIALS_COPY.invalid.location,
  clientEmail: AI_CREDENTIALS_COPY.invalid.clientEmail,
  privateKey: AI_CREDENTIALS_COPY.invalid.privateKey,
}

const draftOf = (current: VertexCredentialsView | null): VertexDraft => ({
  projectId: current?.projectId ?? '',
  location: current?.location ?? DEFAULT_VERTEX_LOCATION,
  clientEmail: current?.clientEmail ?? '',
  privateKey: '',
  chatModel: current?.chatModel ?? VERTEX_CHAT_MODEL_CHOICES[0],
  embeddingModel: current?.embeddingModel ?? VERTEX_EMBEDDING_MODEL_CHOICES[0],
})

const bodyOf = (draft: VertexDraft) => {
  const { privateKey, ...settings } = draft
  return privateKey.trim() === '' ? settings : { ...settings, privateKey }
}

const isVertexField = (value: unknown): value is VertexField => typeof value === 'string' && value in FIELD_ERRORS

const errorsOf = (draft: VertexDraft, needsKey: boolean, issues: readonly { path: readonly PropertyKey[] }[]) => {
  const fromSchema = issues.reduce<VertexErrors>((found, issue) => {
    const field = issue.path[0]
    return isVertexField(field) ? { ...found, [field]: FIELD_ERRORS[field] } : found
  }, {})
  const missingKey = needsKey && draft.privateKey.trim() === ''
  return missingKey ? { ...fromSchema, privateKey: AI_CREDENTIALS_COPY.invalid.privateKeyRequired } : fromSchema
}

const privateKeyHint = (current: VertexCredentialsView | null): string =>
  current === null
    ? AI_CREDENTIALS_COPY.privateKeyNew
    : AI_CREDENTIALS_COPY.privateKeyKept(formatDateTime(new Date(current.secretUpdatedAt)))

const errorProp = (error: string | undefined) => (error === undefined ? {} : { error })

export interface VertexCredentialsFormProps {
  current: VertexCredentialsView | null
  pending: boolean
  error: string | null
  onSubmit: (value: VertexCredentialsUpdate) => void
  onCancel?: () => void
}

interface PasteBoxProps {
  onFill: (fields: Pick<VertexDraft, 'projectId' | 'clientEmail' | 'privateKey'>) => void
}

const PasteBox = ({ onFill }: PasteBoxProps) => {
  const [text, setText] = useState('')
  const [state, setState] = useState<PasteState>('idle')

  const onText = (value: string) => {
    if (value.trim() === '') {
      setText('')
      setState('idle')
      return
    }
    const fields = readServiceAccountKey(value)
    setText(fields === null ? value : '')
    setState(fields === null ? 'invalid' : 'filled')
    if (fields !== null) onFill(fields)
  }

  return (
    <>
      <Field
        id="vertex-json"
        label={AI_CREDENTIALS_COPY.pasteJson}
        hint={AI_CREDENTIALS_COPY.pasteJsonHint}
        {...errorProp(state === 'invalid' ? AI_CREDENTIALS_COPY.pasteJsonInvalid : undefined)}
      >
        {({ id, describedBy }) => (
          <textarea
            id={id}
            className="num"
            rows={3}
            autoComplete="off"
            spellCheck={false}
            aria-describedby={describedBy}
            value={text}
            onChange={(event) => onText(event.target.value)}
          />
        )}
      </Field>
      {state === 'filled' ? (
        <p className="settings-saved text-tone tone-success" role="status">
          {AI_CREDENTIALS_COPY.pasteJsonFilled}
        </p>
      ) : null}
    </>
  )
}

interface TextFieldProps {
  id: string
  field: VertexField
  label: string
  hint?: string
  draft: VertexDraft
  errors: VertexErrors
  onValue: (field: VertexField, value: string) => void
}

const TextField = ({ id, field, label, hint, draft, errors, onValue }: TextFieldProps) => (
  <Field id={id} label={label} {...(hint === undefined ? {} : { hint })} {...errorProp(errors[field])}>
    {({ id: inputId, describedBy }) => (
      <input
        id={inputId}
        className="num"
        autoComplete="off"
        spellCheck={false}
        aria-describedby={describedBy}
        aria-invalid={errors[field] === undefined ? undefined : true}
        value={draft[field]}
        onChange={(event) => onValue(field, event.target.value)}
      />
    )}
  </Field>
)

interface ModelFieldProps {
  id: string
  field: 'chatModel' | 'embeddingModel'
  label: string
  choices: readonly string[]
  draft: VertexDraft
  onValue: (field: VertexField, value: string) => void
}

const ModelField = ({ id, field, label, choices, draft, onValue }: ModelFieldProps) => (
  <Field id={id} label={label}>
    {({ id: inputId }) => (
      <select id={inputId} value={draft[field]} onChange={(event) => onValue(field, event.target.value)}>
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    )}
  </Field>
)

export const VertexCredentialsForm = ({ current, pending, error, onSubmit, onCancel }: VertexCredentialsFormProps) => {
  const [draft, setDraft] = useState<VertexDraft>(() => draftOf(current))
  const [errors, setErrors] = useState<VertexErrors>({})
  const onValue = (field: VertexField, value: string) => setDraft((previous) => ({ ...previous, [field]: value }))
  const shared = { draft, errors, onValue }

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const parsed = VertexCredentialsUpdate.safeParse(bodyOf(draft))
    const found = errorsOf(draft, current === null, parsed.success ? [] : parsed.error.issues)
    setErrors(found)
    if (parsed.success && Object.keys(found).length === 0) onSubmit(parsed.data)
  }

  return (
    <form className="ai-credentials-form" onSubmit={onFormSubmit} noValidate>
      <PasteBox onFill={(fields) => setDraft((previous) => ({ ...previous, ...fields }))} />
      <div className="settings-pair">
        <TextField id="vertex-project" field="projectId" label={AI_CREDENTIALS_COPY.projectId} {...shared} />
        <TextField
          id="vertex-location"
          field="location"
          label={AI_CREDENTIALS_COPY.location}
          hint={AI_CREDENTIALS_COPY.locationHint}
          {...shared}
        />
      </div>
      <TextField id="vertex-email" field="clientEmail" label={AI_CREDENTIALS_COPY.clientEmail} {...shared} />
      <Field id="vertex-key" label={AI_CREDENTIALS_COPY.privateKey} hint={privateKeyHint(current)} {...errorProp(errors.privateKey)}>
        {({ id, describedBy }) => (
          <textarea
            id={id}
            className="num"
            rows={3}
            autoComplete="off"
            spellCheck={false}
            aria-describedby={describedBy}
            aria-invalid={errors.privateKey === undefined ? undefined : true}
            value={draft.privateKey}
            onChange={(event) => onValue('privateKey', event.target.value)}
          />
        )}
      </Field>
      <div className="settings-pair">
        <ModelField
          id="vertex-chat-model"
          field="chatModel"
          label={AI_CREDENTIALS_COPY.chatModel}
          choices={VERTEX_CHAT_MODEL_CHOICES}
          draft={draft}
          onValue={onValue}
        />
        <ModelField
          id="vertex-embedding-model"
          field="embeddingModel"
          label={AI_CREDENTIALS_COPY.embeddingModel}
          choices={VERTEX_EMBEDDING_MODEL_CHOICES}
          draft={draft}
          onValue={onValue}
        />
      </div>
      <CredentialsFormFooter pending={pending} error={error} {...(onCancel === undefined ? {} : { onCancel })} />
    </form>
  )
}
